/**
 * Home Page
 *
 * Main landing page displaying the list of all technology investments.
 * Provides quick access to create new investments and view/edit existing ones.
 * Uses server-side filtering and pagination for scalability.
 */

import {
  useInvestmentsPaginated,
  useInvestmentCounts,
  usePortcos,
  useSegments,
  useTags,
  useRemoveTag,
} from '@medsuite/api-client';
import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../providers/AuthProvider';

import { TagPill, AddTagDropdown } from '../../components/Tags';
import { useDebounce } from '../../hooks/useDebounce';
import { InvestmentImporter } from '../InvestmentEditor/components/InvestmentImporter';

import type { InvestmentFilterParams } from '@medsuite/api-client';
import type { InvestmentWithRelations } from '@medsuite/models';

const PAGE_SIZE = 25;

/**
 * HomePage Component
 *
 * Features:
 * - Page title and description
 * - "Add New Investment" button (top right)
 * - Investment list with server-side filtering and pagination
 * - Quick stats showing total counts from database
 * - Debounced search input
 */
export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Filter state (local state that drives the API query)
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedPortco, setSelectedPortco] = useState<string>('');
  const [selectedSegment, setSelectedSegment] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<{ tag_id: number; tag_name: string }[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showImporter, setShowImporter] = useState(false);
  const [removingTagId, setRemovingTagId] = useState<number | null>(null);
  const [showMyInvestments, setShowMyInvestments] = useState(false);

  // Debounce search text to avoid excessive API calls
  const debouncedSearchText = useDebounce(searchText, 500);

  // Build filter params for the API
  const filterParams: InvestmentFilterParams = useMemo(() => {
    const params: InvestmentFilterParams = {
      page: currentPage,
      limit: PAGE_SIZE,
      sortBy: 'composite_score',
      sortOrder: 'desc',
    };

    if (selectedStatus) {
      params.status = selectedStatus;
    }
    if (selectedPortco) {
      params.portco_id = parseInt(selectedPortco, 10);
    }
    if (selectedSegment) {
      params.segment_id = parseInt(selectedSegment, 10);
    }
    if (debouncedSearchText) {
      params.search = debouncedSearchText;
    }
    if (selectedTags.length > 0) {
      params.tag_ids = selectedTags.map((t) => t.tag_id).join(',');
    }
    // Research owner filter (independent of status filters, can combine)
    if (showMyInvestments && user?.username) {
      params.research_owner_email = user.username;
    }

    return params;
  }, [
    selectedStatus,
    selectedPortco,
    selectedSegment,
    selectedTags,
    debouncedSearchText,
    currentPage,
    showMyInvestments,
    user?.username,
  ]);

  // Fetch investments with pagination
  const { data: investmentsResult, isLoading, error } = useInvestmentsPaginated(filterParams);

  // Fetch total counts for bucket cards (always shows all investments regardless of filters)
  const { data: counts } = useInvestmentCounts();

  // Fetch reference data for filters
  const { data: portcos = [] } = usePortcos();
  const { data: segments = [] } = useSegments();
  const { data: allTags = [] } = useTags();

  // Remove tag mutation
  const removeTagMutation = useRemoveTag();

  // Extract data and pagination from the result
  const investments = investmentsResult?.data || [];
  const pagination = investmentsResult?.pagination;

  // Sort portcos and segments alphabetically
  const sortedPortcos = useMemo(
    () => [...portcos].sort((a, b) => a.portco_name.localeCompare(b.portco_name)),
    [portcos]
  );
  const sortedSegments = useMemo(
    () => [...segments].sort((a, b) => a.segment_name.localeCompare(b.segment_name)),
    [segments]
  );

  /**
   * Reset to page 1 when filters change
   */
  const handleFilterChange = useCallback(
    (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
      setter(value);
      setCurrentPage(1);
    },
    []
  );

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setSelectedStatus('');
    setSelectedPortco('');
    setSelectedSegment('');
    setSelectedTags([]);
    setSearchText('');
    setShowMyInvestments(false);
    setCurrentPage(1);
  };

  /**
   * Handle status filter click
   */
  const handleStatusClick = (status: string) => {
    handleFilterChange(setSelectedStatus, selectedStatus === status ? '' : status);
  };

  /**
   * Check if any filters are active
   */
  const hasActiveFilters =
    selectedStatus || selectedPortco || selectedSegment || selectedTags.length > 0 || searchText || showMyInvestments;

  /**
   * Handle create new investment
   */
  const handleCreateNew = () => {
    navigate('/investments/new');
  };

  /**
   * Handle clicking a tag pill to add it to the filter
   */
  const handleTagClick = (tagId: number, tagName: string) => {
    // Check if tag is already in the filter
    if (!selectedTags.some((t) => t.tag_id === tagId)) {
      setSelectedTags([...selectedTags, { tag_id: tagId, tag_name: tagName }]);
      setCurrentPage(1);
    }
  };

  /**
   * Handle removing a tag from the filter
   */
  const handleRemoveTagFromFilter = (tagId: number) => {
    setSelectedTags(selectedTags.filter((t) => t.tag_id !== tagId));
    setCurrentPage(1);
  };

  /**
   * Handle removing a tag from an investment
   */
  const handleRemoveTagFromInvestment = async (investmentId: number, tagId: number) => {
    setRemovingTagId(tagId);
    try {
      await removeTagMutation.mutateAsync({ investmentId, tagId });
    } catch (error) {
      console.error('Failed to remove tag:', error);
    } finally {
      setRemovingTagId(null);
    }
  };

  /**
   * Get missing data warnings for an investment
   */
  const getMissingDataWarnings = (investment: InvestmentWithRelations) => {
    const warnings: string[] = [];

    if (!investment.composite_score || investment.composite_score === 0) {
      warnings.push('Missing Score');
    }
    if (!investment.milestone_count) {
      warnings.push('Missing Milestones');
    }
    if (!investment.resource_count) {
      warnings.push('Missing Resources');
    }
    if (!investment.success_measure_count) {
      warnings.push('Missing Measures');
    }
    if (!investment.sponsor_count) {
      warnings.push('Missing Sponsors');
    }
    if (
      !investment.prime_objective ||
      !investment.problem_context ||
      !investment.improvement_ideas ||
      !investment.assumptions
    ) {
      warnings.push('Missing Context');
    }

    return warnings;
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { classes: string; label: string }> = {
      new: {
        classes: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        label: 'New',
      },
      submitted: {
        classes: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200',
        label: 'Submitted',
      },
      approved: {
        classes: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200',
        label: 'Approved',
      },
      deferred: {
        classes: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200',
        label: 'Deferred',
      },
      rejected: {
        classes: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200',
        label: 'Rejected',
      },
      planned: {
        classes: 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200',
        label: 'Planned',
      },
      in_progress: {
        classes: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200',
        label: 'In Progress',
      },
      complete: {
        classes: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200',
        label: 'Complete',
      },
      cancelled: {
        classes: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        label: 'Cancelled',
      },
    };

    const config = statusConfig[status] || statusConfig.new;
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.classes}`}
      >
        {config.label}
      </span>
    );
  };

  /**
   * Pagination controls component
   */
  const PaginationControls = () => {
    if (!pagination || pagination.totalPages <= 1) {
      return null;
    }

    const { page, totalPages, total, hasNext, hasPrev } = pagination;

    // Calculate range of items shown
    const startItem = (page - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(page * PAGE_SIZE, total);

    // Generate page numbers to show
    const getPageNumbers = () => {
      const pages: (number | 'ellipsis')[] = [];
      const showPages = 5; // Max pages to show

      if (totalPages <= showPages + 2) {
        // Show all pages if total is small
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);

        // Calculate start and end of middle section
        let start = Math.max(2, page - 1);
        let end = Math.min(totalPages - 1, page + 1);

        // Adjust if at edges
        if (page <= 3) {
          end = Math.min(totalPages - 1, showPages - 1);
        } else if (page >= totalPages - 2) {
          start = Math.max(2, totalPages - showPages + 2);
        }

        // Add ellipsis before middle section if needed
        if (start > 2) {
          pages.push('ellipsis');
        }

        // Add middle pages
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        // Add ellipsis after middle section if needed
        if (end < totalPages - 1) {
          pages.push('ellipsis');
        }

        // Always show last page
        pages.push(totalPages);
      }

      return pages;
    };

    return (
      <div className="my-6 flex items-center justify-between rounded-lg bg-white dark:bg-gray-800 px-4 py-3 shadow sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage(page - 1)}
            disabled={!hasPrev}
            className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(page + 1)}
            disabled={!hasNext}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing <span className="font-medium">{startItem}</span> to{' '}
              <span className="font-medium">{endItem}</span> of{' '}
              <span className="font-medium">{total}</span> results
            </p>
          </div>
          <div>
            <nav
              className="isolate inline-flex -space-x-px rounded-md shadow-sm"
              aria-label="Pagination"
            >
              <button
                onClick={() => setCurrentPage(page - 1)}
                disabled={!hasPrev}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {getPageNumbers().map((pageNum, idx) =>
                pageNum === 'ellipsis' ? (
                  <span
                    key={`ellipsis-${idx}`}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600"
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 focus:outline-offset-0 ${
                      pageNum === page
                        ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                        : 'text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              )}

              <button
                onClick={() => setCurrentPage(page + 1)}
                disabled={!hasNext}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Product Governance
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Manage and track all investment requests across your organization
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              {/* Import from Excel Button */}
              <button
                type="button"
                onClick={() => setShowImporter(true)}
                className="inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-offset-gray-900 transition-colors"
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  />
                </svg>
                Import from Excel
              </button>

              {/* Add New Investment Button */}
              <button
                type="button"
                onClick={handleCreateNew}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-offset-gray-900 transition-colors"
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                </svg>
                Add New Investment
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats - Now Clickable Filters with Database Counts */}
        {counts && (
          <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {/* Total Investments - No filter */}
            <button
              onClick={() => handleFilterChange(setSelectedStatus, '')}
              className={`overflow-hidden rounded-lg px-4 py-5 shadow sm:p-6 text-left transition-all ${
                selectedStatus === ''
                  ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500 dark:ring-blue-400'
                  : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                Total Investments
              </dt>
              <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {counts.total}
              </dd>
            </button>

            {/* Submitted */}
            <button
              onClick={() => handleStatusClick('submitted')}
              className={`relative overflow-hidden rounded-lg px-4 py-5 shadow sm:p-6 text-left transition-all ${
                selectedStatus === 'submitted'
                  ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500 dark:ring-blue-400'
                  : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                Submitted
              </dt>
              <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {counts.submitted}
              </dd>
              {selectedStatus === 'submitted' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange(setSelectedStatus, '');
                  }}
                  className="absolute top-2 right-2 rounded-full p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Clear filter"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </button>

            {/* Approved */}
            <button
              onClick={() => handleStatusClick('approved')}
              className={`relative overflow-hidden rounded-lg px-4 py-5 shadow sm:p-6 text-left transition-all ${
                selectedStatus === 'approved'
                  ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500 dark:ring-blue-400'
                  : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                Approved
              </dt>
              <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {counts.approved}
              </dd>
              {selectedStatus === 'approved' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange(setSelectedStatus, '');
                  }}
                  className="absolute top-2 right-2 rounded-full p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Clear filter"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </button>

            {/* My Investments */}
            <button
              onClick={() => {
                setShowMyInvestments(!showMyInvestments);
                setCurrentPage(1);
              }}
              className={`relative overflow-hidden rounded-lg px-4 py-5 shadow sm:p-6 text-left transition-all ${
                showMyInvestments
                  ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500 dark:ring-blue-400'
                  : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                My Investments
              </dt>
              <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {counts.my_investments}
              </dd>
              {showMyInvestments && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMyInvestments(false);
                    setCurrentPage(1);
                  }}
                  className="absolute top-2 right-2 rounded-full p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
                  title="Clear filter"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </button>
          </div>
        )}

        {/* Filter and Search Bar */}
        <div className="mb-8 rounded-lg bg-white dark:bg-gray-800 p-4 shadow">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Portco Filter */}
            <div>
              <label
                htmlFor="portco-filter"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Portfolio Company
              </label>
              <select
                id="portco-filter"
                value={selectedPortco}
                onChange={(e) => handleFilterChange(setSelectedPortco, e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
              >
                <option value="">All Portfolio Companies</option>
                {sortedPortcos.map((portco) => (
                  <option key={portco.portco_id} value={portco.portco_id}>
                    {portco.portco_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Segment Filter */}
            <div>
              <label
                htmlFor="segment-filter"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Segment
              </label>
              <select
                id="segment-filter"
                value={selectedSegment}
                onChange={(e) => handleFilterChange(setSelectedSegment, e.target.value)}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
              >
                <option value="">All Segments</option>
                {sortedSegments.map((segment) => (
                  <option key={segment.segment_id} value={segment.segment_id}>
                    {segment.segment_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags Filter */}
            <div>
              <label
                htmlFor="tags-filter"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Tags
              </label>
              <select
                id="tags-filter"
                value=""
                onChange={(e) => {
                  const tagId = parseInt(e.target.value, 10);
                  const tag = allTags.find((t) => t.tag_id === tagId);
                  if (tag && !selectedTags.some((t) => t.tag_id === tagId)) {
                    setSelectedTags([
                      ...selectedTags,
                      { tag_id: tag.tag_id, tag_name: tag.tag_name },
                    ]);
                    setCurrentPage(1);
                  }
                }}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
              >
                <option value="">
                  {selectedTags.length > 0
                    ? `${selectedTags.length} tag(s) selected`
                    : 'Filter by tags...'}
                </option>
                {allTags
                  .filter((tag) => !selectedTags.some((t) => t.tag_id === tag.tag_id))
                  .sort((a, b) => a.tag_name.localeCompare(b.tag_name))
                  .map((tag) => (
                    <option key={tag.tag_id} value={tag.tag_id}>
                      {tag.tag_name} ({tag.investment_count})
                    </option>
                  ))}
              </select>
              {/* Selected Tags Pills */}
              {selectedTags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedTags.map((tag) => (
                    <TagPill
                      key={tag.tag_id}
                      tagId={tag.tag_id}
                      tagName={tag.tag_name}
                      showRemove
                      onRemove={() => handleRemoveTagFromFilter(tag.tag_id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Search Box */}
            <div>
              <label
                htmlFor="search-text"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Search
              </label>
              <div className="relative">
                <input
                  id="search-text"
                  type="text"
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search investments..."
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 pl-10 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <svg
                    className="h-4 w-4 text-gray-400 dark:text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                    />
                  </svg>
                </div>
                {/* Loading indicator while debouncing */}
                {searchText !== debouncedSearchText && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {pagination ? (
                  <>
                    Showing {investments.length} of {pagination.total} filtered investments
                  </>
                ) : (
                  <>Filters applied</>
                )}
              </span>
              <button
                onClick={handleClearFilters}
                className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                <svg
                  className="mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Top Pagination Controls - only shown when pagination is needed */}
        <PaginationControls />

        {/* Investment Cards */}
        {error ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg
                className="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Error loading investments
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
          </div>
        ) : isLoading ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-500"></div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading investments...</p>
          </div>
        ) : investments.length === 0 && hasActiveFilters ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              No investments match your filters
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Try adjusting your filters to see more results
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Clear all filters
              </button>
            </div>
          </div>
        ) : investments.length === 0 ? (
          <div className="rounded-lg bg-white dark:bg-gray-800 p-8 text-center shadow">
            <svg
              className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              No investments yet
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating a new technology investment request
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={handleCreateNew}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:focus-visible:outline-offset-gray-900"
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                </svg>
                Add New Investment
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {investments.map((investment) => (
                <div
                  key={investment.investment_id}
                  className="rounded-lg bg-white dark:bg-gray-800 shadow"
                >
                  <div className="p-6">
                    {/* Header Row: Title and Status */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3
                          className="text-lg font-bold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                          onClick={() => navigate(`/investments/${investment.investment_id}`)}
                        >
                          {investment.title}
                        </h3>
                      </div>
                      {/* Status */}
                      <div className="ml-4 flex-shrink-0">{getStatusBadge(investment.status)}</div>
                    </div>

                    {/* Details Grid */}
                    <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3 lg:grid-cols-5">
                      <div>
                        <div className="font-bold text-gray-700 dark:text-gray-400">
                          Composite Score
                        </div>
                        <div className="mt-1 text-gray-900 dark:text-gray-100">
                          {investment.composite_score !== null
                            ? investment.composite_score.toFixed(1)
                            : '—'}
                        </div>
                      </div>
                      {investment.portco_name && (
                        <div>
                          <div className="font-bold text-gray-700 dark:text-gray-400">
                            Portfolio Company
                          </div>
                          <div className="mt-1 text-gray-900 dark:text-gray-100">
                            {investment.portco_name}
                          </div>
                        </div>
                      )}
                      {investment.segment_name && (
                        <div>
                          <div className="font-bold text-gray-700 dark:text-gray-400">Segment</div>
                          <div className="mt-1 text-gray-900 dark:text-gray-100">
                            {investment.segment_name}
                          </div>
                        </div>
                      )}
                      {investment.company_name && (
                        <div>
                          <div className="font-bold text-gray-700 dark:text-gray-400">Company</div>
                          <div className="mt-1 text-gray-900 dark:text-gray-100">
                            {investment.company_name}
                          </div>
                        </div>
                      )}
                      {investment.submitted_date && (
                        <div>
                          <div className="font-bold text-gray-700 dark:text-gray-400">
                            Submitted Date
                          </div>
                          <div className="mt-1 text-gray-900 dark:text-gray-100">
                            {new Date(investment.submitted_date).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-gray-700 dark:text-gray-400">Regulatory</div>
                        <div className="mt-1 text-gray-900 dark:text-gray-100">
                          {investment.is_regulatory_requirement ? 'Yes' : 'No'}
                        </div>
                      </div>
                      {investment.program_name && (
                        <div>
                          <div className="font-bold text-gray-700 dark:text-gray-400">Program</div>
                          <div className="mt-1 text-gray-900 dark:text-gray-100">
                            {investment.program_name}
                          </div>
                        </div>
                      )}
                      {investment.created_at && (
                        <div>
                          <div className="font-bold text-gray-700 dark:text-gray-400">Created</div>
                          <div className="mt-1 text-gray-900 dark:text-gray-100">
                            {new Date(investment.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      {investment.updated_at && (
                        <div>
                          <div className="font-bold text-gray-700 dark:text-gray-400">
                            Last Updated
                          </div>
                          <div className="mt-1 text-gray-900 dark:text-gray-100">
                            {new Date(investment.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      {investment.research_owners && investment.research_owners.length > 0 && (
                        <div>
                          <div className="font-bold text-gray-700 dark:text-gray-400">
                            Research Owner{investment.research_owners.length > 1 ? 's' : ''}
                          </div>
                          <div className="mt-1 text-gray-900 dark:text-gray-100">
                            {investment.research_owners.join(', ')}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Prime Objective Preview */}
                    {investment.prime_objective && (
                      <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="font-bold text-gray-700 dark:text-gray-400 text-sm">
                          Prime Objective
                        </div>
                        <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                          {investment.prime_objective}
                        </div>
                      </div>
                    )}

                    {/* Tags Section */}
                    <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Existing Tags */}
                        {investment.tags && investment.tags.length > 0 && (
                          <>
                            {investment.tags.map((tag) => (
                              <TagPill
                                key={tag.tag_id}
                                tagId={tag.tag_id}
                                tagName={tag.tag_name}
                                showRemove
                                onClick={handleTagClick}
                                onRemove={(tagId) =>
                                  handleRemoveTagFromInvestment(investment.investment_id, tagId)
                                }
                                isRemoving={removingTagId === tag.tag_id}
                              />
                            ))}
                          </>
                        )}
                        {/* Add Tag Dropdown */}
                        <AddTagDropdown
                          investmentId={investment.investment_id}
                          existingTags={(investment.tags || []).map((t) => ({
                            investment_tag_id: 0,
                            investment_id: investment.investment_id,
                            tag_id: t.tag_id,
                            tag_name: t.tag_name,
                          }))}
                        />
                      </div>
                    </div>

                    {/* Action Icons and Warning Pills */}
                    <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
                      {/* Warning Pills - Left aligned */}
                      <div className="flex flex-wrap gap-1.5">
                        {getMissingDataWarnings(investment).map((warning) => (
                          <span
                            key={warning}
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-950 text-red-800 dark:text-red-200"
                          >
                            <span
                              className="rounded-full h-1.5 w-1.5 bg-red-400 dark:bg-red-500"
                              aria-hidden="true"
                            />
                            {warning}
                          </span>
                        ))}
                      </div>

                      {/* Action buttons - Right aligned */}
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/investments/${investment.investment_id}`);
                          }}
                          className="rounded p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                          title="View investment"
                        >
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/investments/${investment.investment_id}/edit`);
                          }}
                          className="rounded p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                          title="Edit investment"
                        >
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <PaginationControls />
          </>
        )}
      </div>

      {/* Import Modal */}
      <InvestmentImporter
        isOpen={showImporter}
        onClose={() => setShowImporter(false)}
        onImportComplete={() => {
          // Modal will close itself, investments list will refresh automatically
          // due to React Query cache invalidation
        }}
      />
    </div>
  );
}

export default HomePage;
